import { graphql, readInlineData } from 'react-relay';

import {
    PricingGuidancePriceRange,
    pricingGuidanceHelpers_item$key,
    pricingGuidanceHelpers_item$data,
} from './__generated__/pricingGuidanceHelpers_item.graphql';
import { pricingGuidanceHelpers_viewer$key } from './__generated__/pricingGuidanceHelpers_viewer.graphql';

const priceRanges: PricingGuidancePriceRange[] = [
    'OUT_OF_RANGE_LOW',
    'LOWER_RANGE',
    'NEAR_MEDIAN_LOW',
    'MEDIAN',
    'NEAR_MEDIAN_HIGH',
    'HIGHER_RANGE',
    'OUT_OF_RANGE_HIGH',
];

const viewerFragment = graphql`
    fragment pricingGuidanceHelpers_viewer on Viewer @inline {
        pdpSiderailSoldPricingGuidanceLinkFF: featureFlag(
            feature: "pdpSiderailSoldPricingGuidanceLink"
        )
    }
`;

const itemFragment = graphql`
    fragment pricingGuidanceHelpers_item on Item @inline {
        pricingGuidance(isTrade: $isTrade) {
            pricingGuidanceType
            priceRange
            price {
                convertedAmountList {
                    amount
                    currency
                }
            }
        }
        displayPrice(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) {
            convertedAmountList {
                amount
                currency
            }
        }
    }
`;

/**
 * TOF-1570. Note: the ML team is working on new price recommendations that will replace the current implementation.
 * The endpoint will have price caps included. Given that we will only be using the existing endpoint for so much longer
 * and that BE is constrained on bandwidth, we’ve opted to add this logic on the FE as opposed to updating the endpoint
 * logic on the BE. When we switch over to the ML model, we should remove this logic.
 */
const priceLimits = {
    USD: 50000,
    EUR: 47500,
    GBP: 41000,
};

const isPriceAboveLimit = ({ price, currency }: { price: number; currency: string }): boolean => {
    const limit = priceLimits[currency as keyof typeof priceLimits];

    if (limit && price >= limit) {
        return true;
    }

    return false;
};

export const getPriceAmountByCurrency = ({
    price,
    currency,
}: {
    price?: NonNullable<pricingGuidanceHelpers_item$data['pricingGuidance']>['price'];
    currency: string;
}): number => {
    const priceAmount = (price?.convertedAmountList || []).find(
        convertedAmount => convertedAmount?.currency === currency
    );

    return Math.floor(priceAmount?.amount || 0);
};

export const showPricingGuidance = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): boolean => {
    const item = readInlineData(itemFragment, itemRef);
    const { pricingGuidance, displayPrice = null } = item;

    if (!pricingGuidance) {
        return false;
    }

    const price = getPriceAmountByCurrency({ price: displayPrice?.[0], currency });

    if (!price) {
        return false;
    }

    if (isPriceAboveLimit({ price, currency })) {
        return false;
    }

    return true;
};

export const showSoldPricingGuidance = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): boolean => {
    const item = readInlineData(itemFragment, itemRef);
    const { pricingGuidanceType } = item?.pricingGuidance || {};

    if (pricingGuidanceType === 'MARKETPLACE_RETAIL') {
        return showPricingGuidance({ item: itemRef, currency });
    }

    return false;
};

export const showSiderailSoldPricingGuidance = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
}: {
    viewer: pricingGuidanceHelpers_viewer$key;
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): boolean => {
    const viewer = readInlineData(viewerFragment, viewerRef);

    return (
        !!viewer.pdpSiderailSoldPricingGuidanceLinkFF &&
        showSoldPricingGuidance({ item: itemRef, currency })
    );
};

export const getGuidancePriceAmount = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): number => {
    const item = readInlineData(itemFragment, itemRef);
    const { pricingGuidance, displayPrice } = item || {};
    const { price } = pricingGuidance || {};

    return getPriceAmountByCurrency({ price: price || displayPrice?.[0], currency });
};

export function isPriceRangeOrHigher({
    item: itemRef,
    targetPriceRange,
}: {
    item: pricingGuidanceHelpers_item$key;
    targetPriceRange: PricingGuidancePriceRange;
}): boolean {
    const item = readInlineData(itemFragment, itemRef);
    const { priceRange } = item?.pricingGuidance || {};

    return !!priceRange && priceRanges.indexOf(priceRange) >= priceRanges.indexOf(targetPriceRange);
}
